import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header/Header';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsGoogleAccount(false);

    if (!email) {
      setError('Email address is required.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Store the success message from the server
        setError('');
        setIsGoogleAccount(false);
      } else {
        // Check if this is a Google Sign-In account error
        const errorMessage = data.message || 'Failed to send password reset email. Please try again.';
        if (errorMessage === 'Please sign in with Google to access your account.') {
          setIsGoogleAccount(true);
          setError('');
          setSuccess(false);
        } else {
          setError(errorMessage);
          setIsGoogleAccount(false);
          setSuccess(false);
        }
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
      setIsGoogleAccount(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      <Header showSignUp={true} showLogIn={false} />
      
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-text-light-primary dark:text-text-primary">
              Forgot your password?
            </h2>
            <p className="mt-2 text-center text-sm text-text-light-secondary dark:text-text-secondary">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>
          
          <div className="bg-surface-light dark:bg-background-card p-8 rounded-lg shadow-lg border border-border-light dark:border-border-color">
            {success ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Please check your email for a password reset link.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors"
                >
                  Back to Login
                </button>
              </div>
            ) : isGoogleAccount ? (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    Please sign in with Google to access your account.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div>
                  <label className="block text-sm font-medium text-text-light-secondary dark:text-text-secondary" htmlFor="email">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      autoComplete="email"
                      className={`w-full p-3 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                        error 
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                          : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                      }`}
                      id="email"
                      name="email"
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                    />
                  </div>
                  {error && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
                  )}
                </div>
                
                <div>
                  <button
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            )}
          </div>
          
          <p className="mt-10 text-center text-xs text-text-light-secondary dark:text-text-secondary">
            Remember your password?{' '}
            <Link className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors" to="/login">
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;

